import { useState, useEffect, useCallback } from 'preact/hooks';

/*
 * This object defines default breakpoints for screens and containers.
 */
export const defaultBreakpoints = {
  sm: '0px' as const,
  md: '600px' as const,
  lg: '1024px' as const,
  xl: '1440px' as const
};

export type Breakpoints = Record<string, string>;

/*
 * Simple custom hook which returns the current breakpoint based on viewport width, "exclusive" meaning only the breakpoint matching the width will be returned.
 * The breakpoints are returned as an object that is a set of boolean values keyed to each breakpoint.
 * The window/viewport min-width is matched against a range of pixel values defined as the breakpoints.
 */
export function useBreakpointsExclusive(breakpoints: Breakpoints = defaultBreakpoints) {
  // this handler will return current breakpoints as a single object we can watch, rather than looping through each breakpoint key
  const queryMatches = useCallback(() => {
    let activeBreakpoint: string | null = null;
    const currentMatches: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(breakpoints).reverse()) {
      if (window.matchMedia(`(min-width: ${value})`).matches) {
        activeBreakpoint = key;
        break; // Stop at the first match
      }
    }
    Object.keys(breakpoints).forEach((key) => {
      currentMatches[key] = key === activeBreakpoint;
    });
    return currentMatches;
  }, [breakpoints]);

  const [matches, setMatches] = useState(() => queryMatches());

  useEffect(() => {
    const mqls = Object.values(breakpoints).map((value) =>
      window.matchMedia(`(min-width: ${value})`)
    );

    const onChange = () => {
      setMatches(queryMatches());
    };

    mqls.forEach((mql) => mql.addEventListener('change', onChange));

    return () => {
      mqls.forEach((mql) => mql.removeEventListener('change', onChange));
    };
  }, [breakpoints, queryMatches]);

  return matches;
}
